import { useRef, useCallback } from 'react'
import { aiStudioService } from '../service/aiStudio'

interface VideoPollingCallbacks {
  onVideoCompleted?: (videoId: string, resultVideoUrl: string) => void
  onVideoFailed?: (videoId: string, errorMessage: string) => void
  onStatusUpdate?: (videoId: string, status: string) => void
}

export const useVideoPolling = (callbacks?: VideoPollingCallbacks) => {
  const videoPollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const startVideoPolling = useCallback((requestId: string) => {
    console.log('🔄 Video polling started for requestId:', requestId)

    // 이미 해당 requestId에 대한 폴링이 진행 중이라면 중단
    if (videoPollingIntervalsRef.current.has(requestId)) {
      clearInterval(videoPollingIntervalsRef.current.get(requestId)!)
    }

    const interval = setInterval(async () => {
      try {
        console.log('⏰ Polling video status for requestId:', requestId)
        const response = await aiStudioService.getVideoSynthesisRequestStatus(
          parseInt(requestId)
        )

        // APIResponse 구조에서 데이터 추출
        let status = response

        if (!status) {
          console.log('❌ No status data received from video polling')
          return
        }

        console.log('📊 Video current status:', status.status)

        // 상태 업데이트 콜백 호출
        if (callbacks?.onStatusUpdate) {
          callbacks.onStatusUpdate(requestId, status.status)
        }

        if (status.status === 'completed') {
          // 이 requestId의 폴링 중단
          const currentInterval = videoPollingIntervalsRef.current.get(requestId)
          if (currentInterval) {
            clearInterval(currentInterval)
            videoPollingIntervalsRef.current.delete(requestId)
          }

          console.log('🎉 Video generation completed for requestId:', requestId)
          console.log('🎬 Video result URL:', status.resultVideoUrl)

          // 완료 콜백 호출
          if (callbacks?.onVideoCompleted && status.resultVideoUrl) {
            callbacks.onVideoCompleted(requestId, status.resultVideoUrl)
          }
        } else if (status.status === 'failed') {
          // 이 requestId의 폴링 중단
          const currentInterval = videoPollingIntervalsRef.current.get(requestId)
          if (currentInterval) {
            clearInterval(currentInterval)
            videoPollingIntervalsRef.current.delete(requestId)
          }

          console.log('❌ Video generation failed for requestId:', requestId, status.errorMessage)

          // 실패 콜백 호출
          if (callbacks?.onVideoFailed) {
            callbacks.onVideoFailed(requestId, status.errorMessage || 'Unknown error')
          }
        }
      } catch (error) {
        console.error('Error polling video synthesis status:', error)
        // 에러가 발생해도 계속 폴링 (일시적 네트워크 오류일 수 있음)
      }
    }, 3000) // 3초마다 폴링

    // 새로운 interval을 Map에 저장
    videoPollingIntervalsRef.current.set(requestId, interval)
  }, [callbacks])

  const stopVideoPolling = useCallback((requestId: string) => {
    if (videoPollingIntervalsRef.current.has(requestId)) {
      clearInterval(videoPollingIntervalsRef.current.get(requestId)!)
      videoPollingIntervalsRef.current.delete(requestId)
      console.log('🛑 Stopped polling for requestId:', requestId)
    }
  }, [])

  const stopAllVideoPolling = useCallback(() => {
    videoPollingIntervalsRef.current.forEach((interval, requestId) => {
      clearInterval(interval)
      console.log('🛑 Stopped polling for requestId:', requestId)
    })
    videoPollingIntervalsRef.current.clear()
  }, [])

  const getPollingVideos = useCallback(() => {
    return Array.from(videoPollingIntervalsRef.current.keys())
  }, [])

  return {
    startVideoPolling,
    stopVideoPolling,
    stopAllVideoPolling,
    getPollingVideos,
  }
}